"use client";

import React, { useState, useRef, useEffect } from "react";

export interface ModernDatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (dateStr: string) => void;
  label?: string;
  className?: string;
}

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const WEEK_DAYS = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sá"];

export const ModernDatePicker: React.FC<ModernDatePickerProps> = ({
  value,
  onChange,
  label,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse initial date or use today
  const parsedDate = value ? new Date(value + "T00:00:00") : new Date();
  const isValidDate = !isNaN(parsedDate.getTime());
  
  const [currentMonth, setCurrentMonth] = useState(isValidDate ? parsedDate.getMonth() : new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(isValidDate ? parsedDate.getFullYear() : new Date().getFullYear());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayIndex = getFirstDayOfMonth(currentYear, currentMonth);

  const formatDisplayDate = (val: string) => {
    if (!val) return "Selecciona fecha";
    const [y, m, d] = val.split("-");
    const monthIdx = m ? parseInt(m, 10) - 1 : 0;
    const monthName = MONTH_NAMES[monthIdx] || "";
    const dayNum = d ? parseInt(d, 10) : 1;
    return `${dayNum} de ${monthName}, ${y || ""}`;
  };

  const handleSelectDay = (day: number) => {
    const mm = String(currentMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    const dateStr = `${currentYear}-${mm}-${dd}`;
    onChange(dateStr);
    setIsOpen(false);
  };

  const isSelected = (day: number) => {
    if (!value) return false;
    const [y, m, d] = value.split("-");
    if (!y || !m || !d) return false;
    return (
      parseInt(y, 10) === currentYear &&
      parseInt(m, 10) - 1 === currentMonth &&
      parseInt(d, 10) === day
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getFullYear() === currentYear &&
      today.getMonth() === currentMonth &&
      today.getDate() === day
    );
  };

  return (
    <div className={`relative inline-block ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-xs font-mono tracking-wider text-synaptix-cyan mb-1.5 uppercase">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full min-w-[210px] px-4 py-2.5 bg-[#0A1424]/80 hover:bg-[#0E1B30] border border-synaptix-cyan/30 hover:border-synaptix-cyan/70 rounded-xl text-left text-sm font-medium text-white shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-synaptix-cyan/50"
      >
        <span className="flex items-center gap-2">
          <svg className="w-4 h-4 text-synaptix-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {formatDisplayDate(value)}
        </span>
        <svg className={`w-4 h-4 text-synaptix-titanium transition-transform duration-200 ${isOpen ? "rotate-180 text-synaptix-cyan" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 z-50 w-72 bg-[#060E1A]/95 backdrop-blur-xl border border-synaptix-cyan/40 rounded-2xl shadow-[0_20px_50px_rgba(0,240,255,0.15)] p-4 animate-in fade-in zoom-in-95 duration-150">
          {/* Header del Calendario */}
          <div className="flex items-center justify-between mb-4 border-b border-synaptix-cyan/10 pb-2">
            <span className="text-sm font-bold text-white tracking-wide font-grotesk">
              {MONTH_NAMES[currentMonth]} <span className="text-synaptix-cyan font-mono">{currentYear}</span>
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1 text-synaptix-titanium hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1 text-synaptix-titanium hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Días de la semana */}
          <div className="grid grid-cols-7 gap-1 text-center mb-2">
            {WEEK_DAYS.map((day, idx) => (
              <div key={day} className={`text-xs font-semibold ${idx === 0 || idx === 6 ? "text-synaptix-titanium" : "text-synaptix-cyan"}`}>
                {day}
              </div>
            ))}
          </div>

          {/* Días del Mes */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDayIndex }).map((_, i) => (
              <div key={`empty-${i}`} className="h-8" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, idx) => {
              const dayNum = idx + 1;
              const selected = isSelected(dayNum);
              const today = isToday(dayNum);

              return (
                <button
                  key={dayNum}
                  type="button"
                  onClick={() => handleSelectDay(dayNum)}
                  className={`h-8 w-8 rounded-lg text-xs font-semibold flex items-center justify-center transition-all ${
                    selected
                      ? "bg-synaptix-cyan text-black font-bold shadow-[0_0_15px_rgba(0,240,255,0.8)] scale-105"
                      : today
                      ? "border border-synaptix-cyan/50 text-white hover:bg-synaptix-cyan/20"
                      : "text-gray-300 hover:bg-synaptix-cyan/15 hover:text-white"
                  }`}
                >
                  {dayNum}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
